import { Button, Modal, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hook';
import { CopyOutlined, SendOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import api from 'api';
import { getTheme } from 'helpers/getTheme';
import Spinner from 'components/Spinner/Spinner';
import classes from './MessengerIntegrationModal.module.scss';

enum MessengerType {
  TELEGRAM = 'telegram',
  WHATSAPP = 'whatsapp',
}

type MessengerData = {
  api_key: string;
  bot_link: string;
};

type MessengerIntegrationModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const MessengerIntegrationModal = ({ isOpen, onClose }: MessengerIntegrationModalProps) => {
  const { t } = useTranslation();
  const { messageApi } = useAppSelector((store) => store.windowState);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  
  const [activeMessenger, setActiveMessenger] = useState<MessengerType>(MessengerType.TELEGRAM);
  const [isLoading, setIsLoading] = useState(false);
  const [botData, setBotData] = useState<MessengerData | null>(null);

  useEffect(() => {
    if (isOpen && dataRoom?.id) {
      fetchBotData(activeMessenger);
    }
  }, [isOpen, dataRoom?.id, activeMessenger]);

  const fetchBotData = async (messenger: MessengerType) => {
    if (!dataRoom?.id) return;
    
    setIsLoading(true);
    setBotData(null);
    try {
      const response = await api.getRoomMessengerKey({ room_id: dataRoom.id, messenger });
      setBotData((response as any).data as MessengerData);
    } catch (error) {
      messageApi?.error(t('Settings.messengerIntegration.error.fetchKey'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setBotData(null);
    setActiveMessenger(MessengerType.TELEGRAM);
  };

  const handleTabChange = (key: string) => {
    setActiveMessenger(key as MessengerType);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    messageApi?.success(t('Settings.messengerIntegration.success.copied'));
  };

  const openBotLink = () => {
    if (botData?.bot_link) {
      window.open(botData.bot_link, '_blank');
    }
  };

  const getMessengerName = () => {
    return t(`Settings.messengerIntegration.${activeMessenger}`);
  };

  const tabItems = [
    {
      key: MessengerType.TELEGRAM,
      label: t('Settings.messengerIntegration.telegram'),
    },
    // {
    //   key: MessengerType.WHATSAPP,
    //   label: t('Settings.messengerIntegration.whatsapp'),
    // },
  ];

  return (
    <Modal
      title={t('Settings.messengerIntegration.title')}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={600}
      className={getTheme()}
      classNames={{ body: classes.body }}
    >
      <Tabs
        activeKey={activeMessenger}
        items={tabItems}
        onChange={handleTabChange}
        className={classes.tabsContainer}
      />

      {isLoading ? (
        <div className={classes.spinnerContainer}>
          <Spinner size={40} />
        </div>
      ) : botData ? (
        <div className={classes.content}>
          <div className={classes.infoBlock}>
            {t('Settings.messengerIntegration.description', { messenger: getMessengerName() })}
          </div>

          <div className={classes.section}>
            <div className={classes.label}>{t('Settings.messengerIntegration.apiKeyLabel')}</div>
            <div className={classes.valueContainer}>
              <div className={classes.value}>{botData.api_key}</div>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(botData.api_key)}
                className={classes.copyButton}
              >
                {t('Settings.messengerIntegration.copyButton')}
              </Button>
            </div>
            <div className={classes.description}>
              {t('Settings.messengerIntegration.apiKeyDescription')}
            </div>
          </div>

          <div className={classes.section}>
            <div className={classes.label}>{t('Settings.messengerIntegration.botLinkLabel')}</div>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={openBotLink}
              className={classes.botLinkButton}
            >
              {t('Settings.messengerIntegration.openBotButton', { messenger: getMessengerName() })}
            </Button>
            <div className={classes.description}>
              {t('Settings.messengerIntegration.botLinkDescription')}
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default MessengerIntegrationModal;

